import { useEffect, useRef } from 'react'
import { Icon } from '@/icons/Icon'
import { useChatContext } from '@/context/ChatContext'
import { cn } from '@/lib/utils'

// Traffic and search render entirely different trees, and only the search tree
// uses hooks (useRef/useEffect). Splitting them into separate components keeps
// every hook unconditional — a single component with an early `if (isTraffic)`
// return before those hooks would violate the Rules of Hooks.
export default function EmptyResults({ condensed }: { condensed?: boolean }) {
  const { isTraffic } = useChatContext()

  if (isTraffic) return <TrafficEmptyResults condensed={condensed} />
  return <SearchEmptyResults condensed={condensed} />
}

function TrafficEmptyResults({ condensed }: { condensed?: boolean }) {
  const { query } = useChatContext()
  const trimmed = query.trim()

  return (
    <div className={cn('flex min-w-96 flex-col text-13 font-medium', condensed ? 'pb-px' : '')}>
      <div className="flex h-8 items-center px-1.5">
        <p className="truncate font-medium text-gray-700 dark:text-gray-500">
          {trimmed ? (
            <>
              No apps found for{' '}
              <span className="text-gray-500 dark:text-gray-400">'{trimmed}'</span>
            </>
          ) : (
            'Start typing to search for an app'
          )}
        </p>
      </div>
    </div>
  )
}

function SearchEmptyResults({ condensed }: { condensed?: boolean }) {
  const {
    context,
    command,
    collection,
    setShowSuggestion,
    inputRef,
    handleSubmit,
    selectedComparisonApps,
  } = useChatContext()

  const commandText = command?.textContent?.trim() || ''
  const mention = commandText?.slice(1)

  const titleCase = (input: string): string => input.replace(/\b\w/g, (c) => c.toUpperCase())

  const buildComparisonQuery = (): string | null => {
    if (collection !== 'comparison') return null
    const names = selectedComparisonApps
      .map((item) => item.label)
      .filter((label): label is string => Boolean(label))
    if (mention) names.push(titleCase(mention))
    if (names.length < 2) return null
    const joined =
      names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
    return `What are ${joined} each used for?`
  }

  const askAi = () => {
    const comparisonQuery = buildComparisonQuery()
    const override = comparisonQuery ?? mention
    if (!override) return
    handleSubmit({ forceChat: true, queryOverride: override })
  }

  // Keep the keydown handler pointed at the latest askAi without re-binding
  // the global listener on every render (avoids stale closures + listener churn).
  const askAiRef = useRef(askAi)
  askAiRef.current = askAi

  const button = (onClick: () => void, icon: string, label: React.ReactNode) => {
    return (
      <div
        onClick={onClick}
        className={cn(
          'group mt-2 flex h-8 cursor-pointer items-center gap-3 rounded-lg bg-gray-100 pl-[5px] pr-2.5 font-medium text-gray-950 transition-colors hover:bg-primary-50 hover:text-primary-700 dark:bg-white/10 dark:text-white dark:hover:bg-primary-500/20 dark:hover:text-primary-700',
          condensed ? 'rounded-b-xl' : '',
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm transition-colors group-hover:border-primary-100 dark:border-white/15 dark:bg-black dark:group-hover:border-primary-500/10">
          <Icon name={icon} size={12} />
        </div>
        {label}
        <Icon name="arrow-right-small" className="ml-auto" size={12} />
      </div>
    )
  }

  const suggestionPrefix = () => {
    switch (collection) {
      case 'review':
        return 'for'
      default:
        return 'a'
    }
  }

  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      // Only handle Enter if the input is focused and EmptyResults is visible
      if (e.key === 'Enter' && inputRef.current && document.activeElement === inputRef.current) {
        e.preventDefault()
        askAiRef.current()
      }
    }

    document.addEventListener('keydown', handleEnter)
    return () => document.removeEventListener('keydown', handleEnter)
  }, [inputRef])

  return (
    <div className={cn('flex min-w-96 flex-col text-13 font-medium', condensed ? 'pb-px' : '')}>
      <div className="flex h-8 items-center justify-between gap-10 px-1.5">
        <p className="truncate font-medium text-gray-700 dark:text-gray-500">
          No {collection} found for{' '}
          <span className="text-gray-500 dark:text-gray-400">'{commandText}'</span>
        </p>
        {commandText.startsWith('@') && (
          <div
            onClick={() => {
              setShowSuggestion(mention)
            }}
            className="underlined primary truncate font-medium"
          >
            Suggest {suggestionPrefix()} {collection !== null ? collection : 'review'}
          </div>
        )}
      </div>

      {context === 'search' && (
        <>
          <div className="my-2 px-1.5">
            <hr className="h-px w-full border-0 bg-gray-100 dark:bg-white/10"></hr>
          </div>

          {button(
            askAi,
            'sparkle-ai',
            <p>
              Ask Efficient AI about{' '}
              <span className="text-gray-500 transition-colors group-hover:text-primary-500 dark:text-gray-400">
                '{commandText}'
              </span>
            </p>,
          )}
        </>
      )}
    </div>
  )
}
